import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import JsonLd from '@/components/base/JsonLd';
import SeriesCard from '@/components/feature/SeriesCard';
import { seriesList, categoryMap, type Series } from '@/mocks/series';
import { statusInfo } from '@/utils/series';

type StatusKey = Series['status'];
const statusTabs: { key: 'all' | StatusKey; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'ongoing', label: '追更中' },
  { key: 'finished', label: '已完结' },
  { key: 'coming', label: '待开播' },
];

const collectionSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '吃瓜每日 · 追更大厅',
  inLanguage: 'zh-CN',
  description: '浏览吃瓜每日全部娱乐瓜局连续剧系列，按类型、状态与热度筛选追更。',
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
    { '@type': 'ListItem', position: 2, name: '追更大厅' },
  ],
};

export default function Chasing() {
  const [params, setParams] = useSearchParams();
  const cat = params.get('cat') || 'all';
  const status = (params.get('status') || 'all') as 'all' | StatusKey;
  const qParam = params.get('q') || '';
  const [kw, setKw] = useState(qParam);

  useEffect(() => {
    setKw(qParam);
  }, [qParam]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (!value) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const q = kw.trim().toLowerCase();
  const list = seriesList.filter((s) => {
    if (cat !== 'all' && s.categoryKey !== cat) return false;
    if (status !== 'all' && s.status !== status) return false;
    if (q) {
      const hay = `${s.title} ${s.categoryName} ${s.summary} ${s.tags.join(' ')}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const activeCat = categoryMap.find((c) => c.key === cat);
  const resetAll = () => {
    setKw('');
    setParams({}, { replace: true });
  };

  return (
    <Layout>
      <JsonLd data={[collectionSchema, breadcrumbSchema]} />
      {/* 页头横幅 */}
      <section className="bg-gradient-to-br from-secondary-100 via-background-100 to-accent-100 pb-10 pt-24 md:pb-14 md:pt-28">
        <div className="mx-auto w-full px-4 md:px-6">
          <div className="mx-auto w-full max-w-6xl">
            <nav aria-label="面包屑" className="text-xs text-foreground-500">
              <ol className="flex flex-wrap items-center gap-1">
                <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
                <li aria-hidden="true"><i className="ri-arrow-right-s-line text-foreground-300"></i></li>
                <li className="font-medium text-foreground-700">追更大厅</li>
              </ol>
            </nav>
            <h1 className="mt-4 font-heading text-3xl text-foreground-950 md:text-5xl">追更大厅</h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base">
              所有成系列的娱乐瓜局都在这：想追竞演、情感、影视还是商业，挑一部你的下饭连续剧，顺着时间线一集一集追下去。
            </p>
          </div>
        </div>
      </section>

      {/* 筛选区 */}
      <section className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
        <div className="mx-auto w-full max-w-6xl">
          <div className="flex flex-col gap-5 rounded-2xl border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex flex-col items-stretch justify-between gap-3 lg:flex-row lg:items-center">
              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-1 text-sm font-bold text-foreground-950">类型</span>
                <button
                  type="button"
                  onClick={() => updateParam('cat', '')}
                  className={`flex items-center whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                    cat === 'all'
                      ? 'bg-primary-500 font-bold text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                  }`}
                >
                  全部
                </button>
                {categoryMap.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => updateParam('cat', c.key)}
                    className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                      cat === c.key
                        ? 'bg-primary-500 font-bold text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                    }`}
                  >
                    <i className={c.icon}></i>
                    {c.name}
                  </button>
                ))}
              </div>

              <form className="relative w-full lg:w-72" role="search" aria-label="搜索系列" onSubmit={(e) => { e.preventDefault(); updateParam('q', kw); }}>
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
                <input
                  value={kw}
                  onChange={(e) => setKw(e.target.value)}
                  placeholder="搜索系列名、类型或标签…"
                  aria-label="搜索系列"
                  className="h-11 w-full rounded-full border border-background-200 bg-background-100/60 pl-10 pr-4 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-300 focus:bg-background-50"
                />
              </form>
            </div>

            <div className="flex flex-col items-start justify-between gap-3 border-t border-background-200/70 pt-4 md:flex-row md:items-center">
              <div className="flex items-center gap-2">
                <span className="mr-1 text-sm font-bold text-foreground-950">状态</span>
                <div className="flex rounded-full bg-background-100 p-1">
                  {statusTabs.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => updateParam('status', t.key === 'all' ? '' : t.key)}
                      className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                        status === t.key
                          ? 'bg-background-50 font-bold text-primary-600 shadow-sm'
                          : 'text-foreground-600 hover:text-primary-600'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-sm text-foreground-500">
                {activeCat ? `「${activeCat.name}」` : '全部类型'}
                {q ? ` 与关键词「${kw.trim()}」` : ''}
                共 <span className="font-bold text-primary-600">{list.length}</span> 个系列
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 结果列表 */}
      <section className="mx-auto w-full px-4 pb-16 md:px-6 md:pb-24">
        <div className="mx-auto w-full max-w-6xl">
          {list.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((s) => (
                <SeriesCard key={s.id} series={s} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-background-300 bg-background-100/60 px-6 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-300">
                <i className="ri-emotion-sad-line"></i>
              </span>
              <h2 className="mt-5 text-lg font-bold text-foreground-950">没有找到匹配的瓜</h2>
              <p className="mt-2 max-w-sm text-sm text-foreground-500">
                换个关键词或类型试试吧，或者看看所有正在追更中的系列。
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={resetAll}
                  className="flex h-11 items-center whitespace-nowrap rounded-full bg-primary-500 px-6 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                >
                  清空筛选
                </button>
                <Link
                  to="/chasing?status=ongoing"
                  className="flex h-11 items-center whitespace-nowrap rounded-full border border-background-200 px-6 text-sm font-bold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  看追更中
                </Link>
              </div>
            </div>
          )}

          {/* 状态说明条 */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 rounded-2xl border border-background-200 bg-background-50 px-5 py-4 text-sm text-foreground-600">
            {Object.values(statusInfo).map((s) => (
              <span key={s.text} className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <span className={`h-2 w-2 rounded-full ${s.dot}`}></span>
                {s.text}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-time-line text-accent-600"></i>
              页面数据为静态演示，将随真实内容库更新
            </span>
          </div>
        </div>
      </section>
    </Layout>
  );
}